import React, { useState, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import Spinner from "../../../components/Spinner";
import { StatusBadge, MomentWrapper, } from '../../../components/helpers';
import { FormFieldSwitch, } from '../../../components/form.fields';
import { useForm, Controller, } from "react-hook-form";
import { Row, Card, Input, Button, Col, Form, Table, Popconfirm, } from 'antd';
import notification from '../../../services/notification';
import { ChangePassword, GetUserMFAToken, EnableUserMFA, UserDetails, DeleteUserIPLocation, } from '../../../services/users';
import { ProfileSettingsUpdate, } from '../../../services/profile';
import { DisableUserMFAModal, } from '../../../components/users';
import { QRCodeSVG, } from 'qrcode.react';
import {
    CheckSquareIcon, PowerIcon, XSquareIcon, XCircleIcon,
    CheckCircleIcon, PenIcon, TrashIcon,
} from "@phosphor-icons/react";
import 'moment-timezone';

const ProfileSecurityView = ({
    history,
    ...props
}) => {

    const [buttonDisabled, setButtonDisabled] = useState(false);
    const [formDisabled, setFormDisabled] = useState(true);
    const [mfaDataLoading, setMfaDataLoading] = useState(false);
    const [mfaDataLoaded, setMfaDataLoaded] = useState(false);
    const [mfaData, setMfaData] = useState(undefined);
    const [disableMFAModalOpen, setDisableMFAModalOpen] = useState(false);
    const [userProfileData, setUserProfileData] = useState({});
    const [detailsLoading, setDetailsLoading] = useState(true);

    const changePasswordForm = useForm({ mode: "all" });
    const enableMFAForm = useForm({ mode: "all" });
    const sessionForm = useForm({ mode: "all" });
    const intl = useIntl();
    const customerID = "me";

    useEffect(() => {
        LoadProfileDetails();

        return () => {
            // cleanup
            setUserProfileData({});
            setDetailsLoading(true);
        }
        // below line needed to remove the warning on build.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {

        sessionForm.reset({ user_long_session: userProfileData.user_data?.user_long_session });

        // below line needed to remove the warning on build.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userProfileData]);

    const LoadProfileDetails = () => {
        setDetailsLoading(true);

        // get the user profile details
        UserDetails({ user_id: customerID }, history)
            .then(response => {
                if (response.status) {
                    setUserProfileData(response.data);
                    setDetailsLoading(false);
                } else {
                    history('/dashboard');
                    console.error('error:', response)
                    notification['warning']({
                        message: 'User details',
                        description: "Error on requesting profile settings data",
                    });
                }
            })
            .catch(error => {
                notification['error']({
                    message: 'Account',
                    description: intl.formatMessage({ id: "UNKNOW_ERROR" }),
                });
            });
    };

    const savePassword = (data) => {

        setButtonDisabled(true);

        ChangePassword(data, history)
            .then(response => {
                if (response.status) {
                    notification['success']({
                        message: 'User password',
                        description: intl.formatMessage({ id: response.msg }),
                    });
                    changePasswordForm.reset({ current_password: "", new_password: "", confirm_new_password: "" });

                } else {
                    notification['error']({
                        message: 'User password',
                        description: intl.formatMessage({ id: response.msg }),
                    });
                }
                setButtonDisabled(false);

            })
            .catch(error => {
                console.error('error: ', error);
                notification['error']({
                    message: 'User password',
                    description: intl.formatMessage({ id: error.msg }),
                });
                setButtonDisabled(false);
            });
    };

    const startEnableMFA = () => {
        setMfaDataLoading(true);
        GetUserMFAToken(history)
            .then(response => {
                if (response.status) {
                    setMfaData(response.data);
                    setMfaDataLoaded(true);
                } else {
                    console.error('error: ', response);
                }
                setMfaDataLoading(false);
            })
            .catch(error => {
                console.error('error: ', error);
                notification['error']({
                    message: 'User authentication',
                    description: "An error occurred. Please check the browser console for more information",
                });
            });
    };

    const disableMFA = () => {
        setDisableMFAModalOpen(true);
    };

    const enableMFA = (data) => {
        setMfaDataLoading(true);
        setButtonDisabled(true);
        EnableUserMFA(data, history)
            .then(response => {
                if (response.status) {
                    notification['success']({
                        message: 'User MFA',
                        description: intl.formatMessage({ id: response.msg }),
                    });

                    LoadProfileDetails();
                    setMfaDataLoaded(false);
                    setMfaData(undefined);
                    setMfaDataLoading(false);
                    setButtonDisabled(false);

                } else {
                    notification['error']({
                        message: 'User MFA',
                        description: intl.formatMessage({ id: response.msg }),
                    });
                    setMfaDataLoading(false);
                    setButtonDisabled(false);
                }

            })
            .catch(error => { });
    };

    const saveProfile = (data) => {
        setButtonDisabled(true);

        ProfileSettingsUpdate({ user_id: 'me', ...data })
            .then(response => {
                if (response.status) {
                    notification['success']({
                        message: 'User settings',
                        description: intl.formatMessage({ id: response.msg }),
                    });
                } else {
                    notification['error']({
                        message: 'User settings',
                        description: intl.formatMessage({ id: response.msg }),
                    });
                }
                setButtonDisabled(false);
                setFormDisabled(true);
                LoadProfileDetails();
            })
            .catch(error => { });

    };

    const editSettings = () => {
        setFormDisabled(!formDisabled);
    };

    const deleteIPLocationHandler = (data) => {
        DeleteUserIPLocation({
            user_id: 'me',
            ip_address: data.allowed_ip_address,
        }).then(response => {
            LoadProfileDetails();
        })
    };

    const columns = [
        {
            title: <FormattedMessage id="allowed" />,
            dataIndex: 'allow_user_login',
            key: 'allow_user_login',
            render: (allow_user_login, record) => (
                <div>
                    {allow_user_login ? (
                        <CheckCircleIcon size={20} weight="light" className="mr-1" color="green" />
                    ) : (
                        <XCircleIcon size={20} weight="light" className="mr-1" color="red" />
                    )}
                </div>
            )
        },
        {
            title: <FormattedMessage id="ip-address" />,
            dataIndex: 'allowed_ip_address',
            key: 'allowed_ip_address',
            render: (allowed_ip_address, record) => (
                <span>{allowed_ip_address}</span>
            )
        },
        {
            title: <FormattedMessage id="added" />,
            dataIndex: 'user_ip_added',
            key: 'user_ip_added',
            render: (user_ip_added, record) => (
                <span>
                    <MomentWrapper tz="Europe/Amsterdam" format="DD-MM-YYYY HH:mm:ss" date={user_ip_added} />
                </span>
            )
        },
        {
            title: '',
            dataIndex: 'actions',
            key: 'actions',
            align: 'right',
            render: (user_ip_added, record) => (
                <span>
                    <Popconfirm
                        title="Locatie verwijderen"
                        description="Weet je zeker dat je deze locatie wilt verwijderen?"
                        okText="Yes"
                        cancelText="No"
                        okButtonProps={{ danger: true }}
                        onConfirm={e => deleteIPLocationHandler(record)}
                        icon={
                            <TrashIcon size={16} weight="light"
                                className="mr-1 text-danger"
                            />
                        }
                    >
                        <Button size="small" danger={true}><TrashIcon size={16} /></Button>
                    </Popconfirm>
                </span>
            )
        },
    ];

    return (
        <React.Fragment>
            {detailsLoading ? (<Spinner />) : (

                <Row gutter={[32, 32]}>
                    <Col span={24}>

                        {/* Change password card */}
                        <Card
                            title={<h4 className="card-title"><FormattedMessage id="changepassword" /></h4>}
                            className="my-4"
                            loading={detailsLoading}>

                            <Form
                                labelCol={{ span: 8 }}
                                wrapperCol={{ span: 16 }}>

                                {/* Current password */}
                                <FormattedMessage id="current-password">
                                    {NameField =>
                                        <Controller
                                            control={changePasswordForm.control}
                                            defaultValue=""
                                            name="current_password"
                                            rules={{
                                                required: { value: true, message: "inputfield-FieldIsRequired" },
                                                minLength: { value: 8, message: "inputfield-minLength8" },
                                            }}
                                            render={({
                                                field: { onChange, onBlur, value, name, ref },
                                                fieldState: { error },
                                            }) => (
                                                <Form.Item
                                                    help={error && intl.formatMessage({ id: error?.message })}
                                                    hasFeedback
                                                    validateStatus={error?.message && "error"}
                                                    required
                                                    label={<strong>{NameField}</strong>}>
                                                    <Input
                                                        placeholder={NameField}
                                                        onChange={onChange}
                                                        value={value}
                                                        type="password"
                                                        autoComplete="off"
                                                    />
                                                </Form.Item>
                                            )}
                                        />}
                                </FormattedMessage>

                                {/* new password */}
                                <FormattedMessage id="newpassword">
                                    {NameField =>
                                        <Controller
                                            control={changePasswordForm.control}
                                            defaultValue=""
                                            name="new_password"
                                            rules={{
                                                required: { value: true, message: "inputfield-FieldIsRequired" },
                                                minLength: { value: 8, message: "inputfield-minLength8" },
                                            }}
                                            render={({
                                                field: { onChange, onBlur, value, name, ref },
                                                fieldState: { error },
                                            }) => (
                                                <Form.Item
                                                    help={error && intl.formatMessage({ id: error?.message })}
                                                    hasFeedback
                                                    validateStatus={error?.message && "error"}
                                                    required
                                                    label={<strong>{NameField}</strong>}>
                                                    <Input
                                                        placeholder={NameField}
                                                        onChange={onChange}
                                                        value={value}
                                                        type="password"
                                                        autoComplete="off"
                                                    />
                                                </Form.Item>
                                            )}
                                        />}
                                </FormattedMessage>

                                {/* repeat password */}
                                <FormattedMessage id="repeatpassword">
                                    {NameField =>
                                        <Controller
                                            control={changePasswordForm.control}
                                            defaultValue=""
                                            name="confirm_new_password"
                                            rules={{
                                                required: { value: true, message: "inputfield-FieldIsRequired" },
                                                minLength: { value: 8, message: "inputfield-minLength8" },
                                                validate: {
                                                    compare: value => {
                                                        if (changePasswordForm.watch('new_password') !== value) {
                                                            return 'PasswordsNotEqual';
                                                        } else {
                                                            return undefined;
                                                        };
                                                    },
                                                }
                                            }}
                                            render={({
                                                field: { onChange, onBlur, value, name, ref },
                                                fieldState: { error },
                                            }) => (
                                                <Form.Item
                                                    help={error && intl.formatMessage({ id: error?.message })}
                                                    hasFeedback
                                                    validateStatus={error?.message && "error"}
                                                    required
                                                    label={<strong>{NameField}</strong>}>
                                                    <Input
                                                        placeholder={NameField}
                                                        onChange={onChange}
                                                        value={value}
                                                        type="password"
                                                        autoComplete="off"
                                                    />
                                                </Form.Item>
                                            )}
                                        />}
                                </FormattedMessage>

                                <Form.Item
                                    className="text-center"
                                    wrapperCol={{ span: 20, offset: 2, }}
                                >

                                    <Button
                                        type="primary"
                                        disabled={buttonDisabled}
                                        loading={buttonDisabled}
                                        icon={<CheckSquareIcon size={16} weight="light" />}
                                        className="ant-btn-success"
                                        onClick={changePasswordForm.handleSubmit(savePassword)}
                                    >
                                        <FormattedMessage id="save-password" />
                                    </Button>
                                </Form.Item>
                            </Form>
                        </Card>

                        {/* Profile MFA setup */}
                        <Card
                            title={<h4 className="card-title"><FormattedMessage id="profile-mfa-settings" /></h4>}
                            className="my-4">

                            {/* MFA status */}
                            <Form
                                labelCol={{ span: 8 }}
                                wrapperCol={{ span: 16 }}>

                                {/* show status */}
                                <Form.Item label={<strong><FormattedMessage id="profile-mfa-status" /></strong>}>
                                    {userProfileData.user_mfa_enabled ?
                                        (<StatusBadge icon={<CheckCircleIcon size={16} weight="light" />} status="ACTIVE" />) :
                                        (<StatusBadge icon={<XCircleIcon size={16} weight="light" />} status="DISABLED" />)
                                    }
                                </Form.Item>

                                {/* show spinner on loading QR-Code */}
                                {!mfaDataLoaded && mfaDataLoading && (<Spinner />)}

                                {mfaDataLoaded && (
                                    <React.Fragment>
                                        <Form.Item label={<strong><FormattedMessage id="scan-qr-code" /></strong>}>
                                            <QRCodeSVG value={mfaData.qrcode} />
                                            <p><FormattedMessage id="scan-qr-code-help" /></p>
                                        </Form.Item>

                                        {/* authenticator response code */}
                                        <FormattedMessage id="profile-mfa-authentication-token">
                                            {NameField =>
                                                <Controller
                                                    control={enableMFAForm.control}
                                                    defaultValue=""
                                                    name="user_code"
                                                    rules={{
                                                        required: { value: true, message: "inputfield-FieldIsRequired" },
                                                        minLength: { value: 6, message: "inputfield-FieldLength-6number" },
                                                        maxLength: { value: 6, message: "inputfield-FieldLength-6number" },
                                                    }}
                                                    render={({
                                                        field: { onChange, onBlur, value, name, ref },
                                                        fieldState: { error },
                                                    }) => (
                                                        <Form.Item
                                                            help={error && intl.formatMessage({ id: error?.message })}
                                                            hasFeedback
                                                            validateStatus={error?.message && "error"}
                                                            required
                                                            label={<strong>{NameField}</strong>}>
                                                            <Input.OTP
                                                                inputMode="numeric"
                                                                length={6}
                                                                separator={i => i === 2 && <span >—</span>}
                                                                placeholder={NameField}
                                                                onChange={onChange}
                                                                value={value}
                                                                autoComplete="off"
                                                            />
                                                        </Form.Item>
                                                    )}
                                                />}
                                        </FormattedMessage>
                                    </React.Fragment>
                                )}

                                {/* show buttons, depending on the mfa status */}
                                <Form.Item
                                    className="text-center"
                                    wrapperCol={{ span: 20, offset: 2, }}
                                >

                                    {!userProfileData.user_mfa_enabled ? (

                                        <>
                                            {mfaDataLoaded ? (
                                                // enable mfa
                                                <Button
                                                    disabled={buttonDisabled}
                                                    loading={buttonDisabled}
                                                    onClick={enableMFAForm.handleSubmit(enableMFA)}
                                                    icon={<CheckSquareIcon size={16} weight="light" />}
                                                    className="ant-btn-success">
                                                    <FormattedMessage id="mfa-activate" />
                                                </Button>
                                            ) : (
                                                // disabled, show enable button
                                                <Button
                                                    disabled={buttonDisabled}
                                                    loading={buttonDisabled}
                                                    onClick={() => { startEnableMFA() }}
                                                    icon={<PowerIcon size={16} weight="light" />}
                                                    className="ant-btn-success">
                                                    <FormattedMessage id="mfa-enable" />
                                                </Button>
                                            )}
                                        </>
                                    ) : (
                                        // show disable button
                                        <Button
                                            type="primary"
                                            disabled={buttonDisabled}
                                            loading={buttonDisabled}
                                            icon={<XSquareIcon size={16} weight="light" />}
                                            danger
                                            onClick={() => disableMFA()}>
                                            <FormattedMessage id="mfa-disable" />
                                        </Button>
                                    )}
                                </Form.Item>
                            </Form>
                        </Card>

                        {/* Profile Session life time */}
                        <Card
                            title={<h4 className="card-title"><FormattedMessage id="user-profile-settings" /></h4>}
                            className="my-4"
                            extra={[
                                <React.Fragment key="0">
                                    {formDisabled ? (
                                        <Button
                                            onClick={() => { editSettings() }}
                                            icon={<PenIcon size={16} weight="light" />}>
                                            <FormattedMessage id="settings-edit" />
                                        </Button>
                                    ) : (
                                        <Button
                                            disabled={buttonDisabled}
                                            loading={buttonDisabled}
                                            onClick={sessionForm.handleSubmit(saveProfile)}
                                            className="ant-btn-success">
                                            {!buttonDisabled && <CheckSquareIcon size={16} weight="light" className="mr-1" />}
                                            <FormattedMessage id="settings-save" />
                                        </Button>
                                    )}
                                </React.Fragment>
                            ]}>

                            {/* Session status */}
                            <Form
                                labelCol={{ span: 8 }}
                                wrapperCol={{ span: 16 }}>

                                {/* show long file session */}
                                <FormFieldSwitch
                                    formName="user_long_session"
                                    formLabel="user-long-session-lifetime"
                                    defaultValue={false}
                                    control={sessionForm.control}
                                    disabled={formDisabled}
                                    tooltip='user-long-session-lifetime-info' />
                            </Form>

                            {/* Allowed user IP addresses */}
                            <div className="p-2">
                                <h4><FormattedMessage id="user-allowed-ip-addresses" /></h4>
                            </div>

                            <Table
                                rowKey={record => `${record?.allowed_ip_address}_${record?.user_ip_added}`}
                                columns={columns}
                                dataSource={userProfileData.user_allowed_ip_addresses}
                                pagination={false}
                            />

                        </Card>
                    </Col>
                </Row>
            )}

            {disableMFAModalOpen && (
                <DisableUserMFAModal
                    modalIsOpen={disableMFAModalOpen}
                    modalIsOpenHandler={setDisableMFAModalOpen}
                    refreshData={LoadProfileDetails}
                    userID="me"
                    history={history}
                />
            )}
        </React.Fragment>
    );
};


export default ProfileSecurityView;